import { useMemo } from "react";
import { useFeaturedDeals } from "./useFeaturedDeals";
import type { ProductDto, ProductsQueryDto } from "../../../api";

/**
 * Adapter that returns the curated Deal of the Week list in the same shape as
 * useProducts, so it can drive ProductsGridView / ProductsListView. Filtering
 * + pagination are applied client-side because the curated list is small.
 */
export function useFeaturedDealProducts(query?: ProductsQueryDto) {
  const { data, isLoading, error } = useFeaturedDeals();

  const result = useMemo(() => {
    const products = (data ?? [])
      .map((d) => d.product)
      .filter((p): p is ProductDto => p != null);

    const search = query?.search?.trim().toLowerCase();
    const brandIds = query?.brandIds;
    const categoryIds = query?.categoryIds;

    const filtered = products.filter((p) => {
      if (search) {
        const haystack = [p.displayName, p.name, p.defaultCode]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (brandIds?.length && (!p.brand || !brandIds.includes(p.brand.id))) return false;
      if (categoryIds?.length && (!p.category || !categoryIds.includes(p.category.id))) return false;
      return true;
    });

    const page = query?.page ?? 1;
    const pageSize = query?.pageSize ?? filtered.length;
    const start = (page - 1) * pageSize;
    const paged = filtered.slice(start, start + pageSize);
    const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;

    return {
      data: paged,
      meta: {
        currentPage: page,
        isFirstPage: page <= 1,
        isLastPage: page >= pageCount,
        previousPage: page > 1 ? page - 1 : null,
        nextPage: page < pageCount ? page + 1 : null,
        totalCount: filtered.length,
        pageCount,
      },
    };
  }, [data, query]);

  return {
    data: result,
    isLoading,
    error,
  };
}
